import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { SoundVolumes, SoundMuteState } from 'src/types/domain/Sounds'
import { VOLUME_TYPES, MUTE_TYPES } from 'src/types/domain/Sounds'
import { toSafeVolume } from 'src/utils/volumeUtils'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

setAutoFreeze(false)

interface SoundState {
  isPlayingMusic: boolean
  volumes: SoundVolumes
  muted: SoundMuteState
  currentSongIndex: number
  currentSoundEffects: { type: EffectType; id: string }[]
  currentSoundNotifications: { type: NotificationType; id: string }[]
  currentPlaylistKey: string | null
  /**
   * The crash rising-tension cue. A SUSTAINED cue, unlike everything in
   * `currentSoundEffects` which is fire-and-forget — it loops until the crash,
   * so it needs a start/stop switch rather than a queue entry.
   */
  isCrashCountdownPlaying: boolean
  /**
   * Live gain for that cue, 0..1, as a fraction of its configured volume — it
   * SWELLS with the multiplier rather than arriving at full blast. Quantised
   * upstream so a 200ms poll does not write on every tick.
   */
  crashCountdownGain: number
}

// Sound action interfaces
// Here we use present tense for these actions as we are describing the action
// and not the event that happened. Ex. muteMusic instead of buttonClicked or timerHit.
export interface SoundActions {
  // Master
  changeMasterVolume: (volume: number) => void
  muteMaster: () => void
  unmuteMaster: () => void
  // Race
  changeRaceVolume: (volume: number) => void
  muteRace: () => void
  unmuteRace: () => void
  // Music
  changeMusicVolume: (volume: number) => void
  muteMusic: () => void
  unmuteMusic: () => void
  playMusic: () => void
  stopMusic: () => void
  musicSongChanged: (song: number) => void
  setPlaylistKey: (key: string) => void
  // Effects
  changeEffectsVolume: (volume: number) => void
  muteEffects: () => void
  unmuteEffects: () => void
  playEffect: (effect: EffectType) => void
  /** Start/stop the sustained crash tension cue (see isCrashCountdownPlaying). */
  setCrashCountdown: (playing: boolean, gain?: number) => void
  clearEffect: (effectId: string) => void
  clearAllEffects: () => void
  // Notifications
  playNotification: (notification: NotificationType) => void
  clearNotification: (notificationId: string) => void
  clearAllNotifications: () => void
}

type SoundStore = SoundState & {
  actions: SoundActions
}

export type EffectType =
  | 'panelReveal'
  | 'panelRevealReverse'
  | 'cancel'
  | 'click'
  | 'chip'
  | 'switch'
  | 'betPlaced'
  | 'marbleSelected'
  | 'marbleSelectedReverse'
  | 'bonus'
  | 'reward'
  | 'lightningRound'
  | 'crashBust'
  // Birdie Time's own cues (B23) — registry in components/Sound/birdieEffects.ts
  | 'birdieRoundOpen'
  | 'birdieTick'
  | 'birdiePuttHoled'
  | 'birdiePuttMissed'
  | 'birdieWin'
  | 'birdieBigWin'
  | 'birdieBirdie'
  | 'birdieEagle'

export type NotificationType =
  | 'notification'
  | 'notificationRaceScheduled'
  | 'notificationRaceReady'
  | 'notificationRaceStarting'
  | 'notificationRaceStarted'
  | 'notificationRaceStartedAlt'
  | 'notificationRaceEnded'
  | 'notificationRaceSettled'
  | 'notificationRaceSettledWon1'
  | 'notificationRaceSettledLost1'
  | 'notificationRaceSettledLost2'

const initialState: SoundState = {
  currentSoundEffects: [],
  currentSoundNotifications: [],
  isPlayingMusic: false,
  currentSongIndex: 0,
  currentPlaylistKey: null,
  isCrashCountdownPlaying: false,
  crashCountdownGain: 1,
  volumes: {
    [VOLUME_TYPES.MASTER]: 0.8,
    [VOLUME_TYPES.RACE_SOUNDS]: 1.0,
    [VOLUME_TYPES.SOUND_EFFECTS]: 0.5,
    [VOLUME_TYPES.MUSIC]: 0.5
  },
  muted: {
    [MUTE_TYPES.MASTER]: false,
    [MUTE_TYPES.MUSIC]: false,
    [MUTE_TYPES.RACE_SOUNDS]: false,
    [MUTE_TYPES.SOUND_EFFECTS]: false
  }
}

export const useSoundStore = create<SoundStore>()(
  devtools(
    immer((set, _) => ({
      ...initialState,
      actions: {
        // Volume setters clamp at the store boundary: these values feed Web
        // Audio gain nodes and HTMLMediaElement.volume, which throw on
        // non-finite or out-of-range input (in Safari that throw has crashed
        // the whole page). Non-finite input keeps the previous value.
        changeMasterVolume: (volume: number) => {
          set(
            (state) => {
              state.volumes.master = toSafeVolume(volume, state.volumes.master)
            },
            undefined,
            'changeMasterVolume'
          )
        },
        muteMaster: () => {
          set(
            (state) => {
              state.muted.master = true
            },
            undefined,
            'muteMaster'
          )
        },
        unmuteMaster: () => {
          set(
            (state) => {
              state.muted.master = false
            },
            undefined,
            'unmuteMaster'
          )
        },
        changeRaceVolume: (volume: number) => {
          set(
            (state) => {
              state.volumes.race = toSafeVolume(volume, state.volumes.race)
            },
            undefined,
            'changeRaceVolume'
          )
        },
        muteRace: () => {
          set(
            (state) => {
              state.muted.race = true
            },
            undefined,
            'muteRace'
          )
        },
        unmuteRace: () => {
          set(
            (state) => {
              state.muted.race = false
            },
            undefined,
            'unmuteRace'
          )
        },
        changeEffectsVolume: (volume: number) => {
          set(
            (state) => {
              state.volumes.effects = toSafeVolume(volume, state.volumes.effects)
            },
            undefined,
            'changeEffectsVolume'
          )
        },
        muteEffects: () => {
          set(
            (state) => {
              state.muted.effects = true
            },
            undefined,
            'muteEffects'
          )
        },
        unmuteEffects: () => {
          set(
            (state) => {
              state.muted.effects = false
            },
            undefined,
            'unmuteEffects'
          )
        },
        musicSongChanged: (songIndex: number) => {
          set(
            (state) => {
              state.currentSongIndex = songIndex
            },
            undefined,
            'musicSongChanged'
          )
        },
        setPlaylistKey: (key: string) => {
          set(
            (state) => {
              state.currentPlaylistKey = key
            },
            false,
            'setPlaylistKey'
          )
        },
        changeMusicVolume: (volume: number) => {
          set(
            (state) => {
              state.volumes.music = toSafeVolume(volume, state.volumes.music)
            },
            undefined,
            'changeMusicVolume'
          )
        },
        muteMusic: () => {
          set(
            (state) => {
              state.muted.music = true
            },
            undefined,
            'muteMusic'
          )
        },
        unmuteMusic: () => {
          set(
            (state) => {
              state.muted.music = false
            },
            undefined,
            'unmuteMusic'
          )
        },
        playMusic: () => {
          set(
            (state) => {
              state.isPlayingMusic = true
            },
            undefined,
            'playMusic'
          )
        },
        stopMusic: () => {
          set(
            (state) => {
              state.isPlayingMusic = false
            },
            undefined,
            'stopMusic'
          )
        },
        playEffect: (effect: EffectType) => {
          set(
            (state) => {
              state.currentSoundEffects.push({ type: effect, id: Date.now().toString() })
            },
            undefined,
            { type: 'playEffect', effect } as any
          )
        },
        clearEffect: (effectId: string) => {
          set(
            (state) => {
              state.currentSoundEffects = state.currentSoundEffects.filter(
                (effect) => effect.id !== effectId
              )
            },
            undefined,
            { type: 'clearEffect', effectId } as any
          )
        },
        setCrashCountdown: (playing: boolean, gain?: number) => {
          set(
            (state) => {
              // Idempotent: the cue is DERIVED on a repeating poll, so this is
              // called with the same values over and over. Bailing on no-change
              // keeps it from publishing a store update per tick.
              const nextGain = gain ?? state.crashCountdownGain
              if (
                state.isCrashCountdownPlaying === playing &&
                state.crashCountdownGain === nextGain
              ) {
                return
              }
              state.isCrashCountdownPlaying = playing
              state.crashCountdownGain = nextGain
            },
            undefined,
            'setCrashCountdown'
          )
        },
        clearAllEffects: () => {
          set(
            (state) => {
              state.currentSoundEffects = []
            },
            undefined,
            'clearAllEffects'
          )
        },
        playNotification: (notification: NotificationType) => {
          set(
            (state) => {
              state.currentSoundNotifications.push({
                type: notification,
                id: Date.now().toString()
              })
            },
            undefined,
            'playNotification'
          )
        },
        clearNotification: (notificationId: string) => {
          set(
            (state) => {
              state.currentSoundNotifications = state.currentSoundNotifications.filter(
                (notification) => notification.id !== notificationId
              )
            },
            undefined,
            'clearNotification'
          )
        },
        clearAllNotifications: () => {
          set(
            (state) => {
              state.currentSoundNotifications = []
            },
            undefined,
            'clearAllNotifications'
          )
        }
      }
    })),
    { name: 'SoundStore', enabled: isDevtoolsEnabled() }
  )
)

// Typed selectors
export const useSoundActions = () => useSoundStore((state) => state.actions)

// MASTER
export const useGetMasterVolume = () => useSoundStore((state) => state.volumes[VOLUME_TYPES.MASTER])
export const useGetIsMasterMuted = () => useSoundStore((state) => state.muted[MUTE_TYPES.MASTER])

// "Effectively muted" = nothing can be heard: Master is muted or at 0,
// OR every channel is individually muted or at 0. Drives the toolbar
// mute icon and the dialog's Master toggle so they reflect the real
// aggregate state, not just the standalone master flag.
export const useGetIsEffectivelyMuted = () =>
  useSoundStore((state) => {
    const { volumes: v, muted: m } = state
    if (m[MUTE_TYPES.MASTER] || v[VOLUME_TYPES.MASTER] === 0) return true
    const off = (vol: number, mute: boolean) => mute || vol === 0
    return (
      off(v[VOLUME_TYPES.RACE_SOUNDS], m[MUTE_TYPES.RACE_SOUNDS]) &&
      off(v[VOLUME_TYPES.SOUND_EFFECTS], m[MUTE_TYPES.SOUND_EFFECTS]) &&
      off(v[VOLUME_TYPES.MUSIC], m[MUTE_TYPES.MUSIC])
    )
  })

// RACE SOUNDS
export const useGetRaceSoundsVolume = () =>
  useSoundStore((state) => state.volumes[VOLUME_TYPES.RACE_SOUNDS])
export const useGetIsRaceSoundsMuted = () =>
  useSoundStore((state) => state.muted[MUTE_TYPES.RACE_SOUNDS])

// MUSIC
export const useGetMusicVolume = () => useSoundStore((state) => state.volumes[VOLUME_TYPES.MUSIC])
export const useGetIsMusicMuted = () => useSoundStore((state) => state.muted[MUTE_TYPES.MUSIC])
export const useGetCurrentSongIndex = () => useSoundStore((state) => state.currentSongIndex)
export const useGetIsPlayingMusic = () => useSoundStore((state) => state.isPlayingMusic)
export const useGetCurrentPlaylistKey = () => useSoundStore((state) => state.currentPlaylistKey)

// SOUND EFFECTS
export const useGetCurrentEffects = () => useSoundStore((state) => state.currentSoundEffects)

export const useGetIsCrashCountdownPlaying = () =>
  useSoundStore((state) => state.isCrashCountdownPlaying)

export const useGetCrashCountdownGain = () => useSoundStore((state) => state.crashCountdownGain)
export const useGetEffectsVolume = () =>
  useSoundStore((state) => state.volumes[VOLUME_TYPES.SOUND_EFFECTS])
export const useGetIsEffectsMuted = () =>
  useSoundStore((state) => state.muted[MUTE_TYPES.SOUND_EFFECTS])

// NOTIFICATIONS
export const useGetCurrentNotifications = () =>
  useSoundStore((state) => state.currentSoundNotifications)
// For now use the same volume as sound effects
export const useGetNotificationsVolume = () =>
  useSoundStore((state) => state.volumes[VOLUME_TYPES.SOUND_EFFECTS])
export const useGetIsNotificationsMuted = () =>
  useSoundStore((state) => state.muted[MUTE_TYPES.SOUND_EFFECTS])

// Optional: Add a selector to get all sound settings at once
export const useGetAllSoundSettings = () =>
  useSoundStore((state) => ({
    volumes: state.volumes,
    muted: state.muted
  }))
